import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getTokenData,
  isTokenValid,
  refreshAccessToken,
  removeTokenData,
  saveTokenData,
  TOKEN_KEY,
} from '../../services/auth/AuthService';
import { TokenData, User } from '../../models/auth/TokenData';
import Loader from '../../components/common/loader/Loader';
import useDataLayer, { ILoginDataLayer } from '../../hooks/global/UseDataLayer';
import { useAccount } from '../account/AccountContexts';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (tokenData: TokenData) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readUserId = (rawToken: string | null): number | null => {
  if (!rawToken) {
    return null;
  }

  try {
    return (JSON.parse(rawToken) as TokenData).user?.id ?? null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { refetchData } = useAccount();
  const queryClient = useQueryClient();

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isInitialize, setInitialize] = useState(false);
  // Token podmieniony przez inną kartę — ta czeka na przeładowanie i nie może już nic renderować.
  const [isStale, setStale] = useState(false);
  const { pushDataLayer } = useDataLayer();

  useEffect(() => {
    const init = async () => {
      const tokenData = getTokenData();

      if (tokenData && !isTokenValid(tokenData)) {
        refreshAccessToken()
          .then((newTokenData) => {
            saveTokenData(newTokenData);
            checkAuthenticated();
          })
          // Bez twardego przekierowania na /login — publiczne strony (udostępniony raport, skan)
          // mają się otworzyć jako gość; prywatne i tak odeśle na logowanie `PrivateRoutes`.
          .catch(() => {
            removeTokenData();
            checkAuthenticated();
          });
        return;
      }

      checkAuthenticated();
    };

    init();
  }, []);

  // Token siedzi w localStorage współdzielonym przez cały origin, więc zalogowanie się na inne konto w
  // jednej karcie przestawia żądania wszystkich pozostałych, a ich stan Reacta i cache react-query nadal
  // opisują poprzednie konto — UI miesza dane obu. Przeładowanie jest jedynym pewnym sposobem, żeby
  // karta wróciła do jednej tożsamości.
  useEffect(() => {
    // `storage` odpala się wyłącznie w POZOSTAŁYCH kartach, czyli dokładnie w tych rozjechanych.
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TOKEN_KEY) {
        return;
      }

      // Porównujemy id użytkownika, a nie sam token — interceptor zapisuje tu również odświeżony token
      // TEGO SAMEGO użytkownika i to nie jest przełączenie konta.
      const nextUserId = readUserId(event.newValue);

      // Wyczyszczony token to wylogowanie, nie przełączenie: rozejdzie się po kartach pierwszym 401.
      if (nextUserId === null || nextUserId === readUserId(event.oldValue)) {
        return;
      }

      setStale(true);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Przeładowanie odkładamy, aż użytkownik wejdzie na kartę — inaczej każda otwarta karta rusza po
  // bundle i dane w tej samej sekundzie. Samo `window.location.reload()` nie wystarcza do zatrzymania
  // karty: nawigacja jest asynchroniczna, a `visibilitychange` budzi też refetch react-query (jego
  // listener wisi od utworzenia QueryClient, czyli powyżej tego providera), więc karta zdążyłaby
  // wystrzelić żądania nowym tokenem po zasoby starego konta i wyrenderować ich błędy. Dlatego
  // renderowanie wstrzymuje `isStale` poniżej, a tutaj zostaje sama nawigacja.
  useEffect(() => {
    if (!isStale) {
      return;
    }

    const reloadIfVisible = () => {
      if (document.visibilityState === 'visible') {
        window.location.reload();
      }
    };

    // Karta może być akurat widoczna (dwa okna obok siebie) — wtedy przeładowujemy od razu.
    reloadIfVisible();

    document.addEventListener('visibilitychange', reloadIfVisible);
    return () => document.removeEventListener('visibilitychange', reloadIfVisible);
  }, [isStale]);

  const checkAuthenticated = () => {
    const tokenData = getTokenData();

    if (tokenData && isTokenValid(tokenData)) {
      setAuthenticated(true);
      sendLoginEvent(tokenData.user);
    } else {
      removeTokenData();
      setAuthenticated(false);
    }

    setInitialize(true);
  };

  const login = (tokenData: TokenData) => {
    saveTokenData(tokenData);
    // KOLEJNOŚĆ JEST ISTOTNA: najpierw token, potem czyszczenie. `clear()` każe zamontowanym
    // zapytaniom pobrać się od nowa, więc zrobione przed zapisem poleciałyby jeszcze starym
    // tokenem — a po wylogowaniu wprost w 401 i wymuszone wylogowanie.
    //
    // Czyścimy tutaj, a nie tylko w `logout()`, bo nie każda zmiana tożsamości przez `logout()`
    // przechodzi: wejście w impersonację, SSO, ustawienie hasła czy po prostu zalogowanie się na
    // inne konto bez wylogowania. Cache react-query jest kluczowany po zasobie, nie po koncie, więc
    // bez tego nowy użytkownik zaczyna od danych poprzedniego.
    queryClient.clear();
    // Konto i lista projektów NIE są w cache'u react-query, tylko w zwykłym stanie kontekstu, a
    // `UseAccountData` pomija pobranie, dopóki oba są wypełnione — samo `clear()` zostawiłoby więc
    // uprawnienia, addony i plan poprzedniej tożsamości. To jest ta sama para wywołań co w
    // `logout()`; rozjazd między nimi to dziura czekająca na pierwszą ścieżkę logowania bez
    // przeładowania strony.
    refetchData();
    setAuthenticated(true);
    sendLoginEvent(tokenData.user);
    localStorage.removeItem('magicLinkCooldownUntil');
    localStorage.removeItem('forgotCooldownUntil');
    localStorage.removeItem('resendCooldownUntil');
  };

  const sendLoginEvent = (user: User | null) => {
    if (!user) {
      console.error('Login event failed');
      return;
    }

    pushDataLayer<ILoginDataLayer>({
      event: 'login_successful',
      email_hashed: user.email,
      user_id: user.id.toString(),
    });
  };

  const logout = () => {
    removeTokenData();
    setAuthenticated(false);
    // Druga strona tej samej reguły co w `login` — patrz komentarz tam.
    queryClient.clear();
    refetchData();
  };

  // Odmontowanie aplikacji ucina żądania i rendery oparte na danych poprzedniego konta — bez tego
  // widać błędy z żądań, które poleciały, zanim przeglądarka zdążyła wykonać przeładowanie.
  if (!isInitialize || isStale) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw Error('useAuth must be used with an AuthProvider');
  }

  return context;
};
